import { useState, useEffect, useRef } from 'react'
import { Container, Col, Row, Table, Form } from 'react-bootstrap'
import Tabs from 'react-bootstrap/Tabs'
import Spinner from "react-bootstrap/Spinner"

import COLORS from '../../../../../data/colors'

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileZipper,
  faXmark,
  faFloppyDisk, 
  faPen, 
  faChevronLeft
} from "@fortawesome/free-solid-svg-icons";

import toastConfiguration from "../../../../../data/toastConfiguration"
import { ToastContainer, toast } from "react-toastify"

import useBreakpoint from '../../../../../hooks/useBreakpoint'
import useUser from '../../../../../hooks/useUser'

import { useLazyQuery, useMutation } from '@apollo/client/react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'

import GET_COURSE from '../../../../../graphql/queries/courseCatalogue/getCourse'
import GET_COURSE_FORM from "../../../../../graphql/queries/courseCatalogue/getCourseForm"

import EDIT_COURSE from '../../../../../graphql/mutations/courseCatalogue/editCourse'

import CustomTab from '../../../../../components/CustomTab'
import CustomModal from '../../../../../components/CustomModal'
import CustomButton from '../../../../../components/CustomButton'

import { useTranslation } from 'react-i18next'

import DynamicForm from '../../../../../components/DynamicForm'

import { 
  convertCourseStringToNumbers, 
  isDeepEqual, 
  getPatchDiff,
  parseEntityHeader,
  parseEntityValue 
} from '../../../../../utils/courseCatalogueManager'

const GROUPS = {
  general: [
    "title",
    "code",
    "abbreviation",
    "url",
    "language_of_instruction",
    "description", // textarea
    "course__course_availability",
  ],
  curriculum: [
    "learning_outcomes", // textarea
    "course__course_content", // textarea
    "course__bibliography", // textarea
  ],
  academic: [
    "course__academic_term",
    "course__ects",
    "course__assessment_method",
    "course__elm_mode_of_learning",
    "course__elm_learning_schedule",
    "course__elm_lo_type",
    "course__elm_assessment_type",
    "course__elm_activity_type",
    "course__teaching_method",
  ],
  prerequisites: [
    "course__prerequisite_course",
    "course__related_programme",
    "course__other_prerequisites", // textarea
    "course__restricted_to_local",
    "course__restricted_to_alliance"
  ],
  classification: [
    "course__local_study_field",
    "course__isced_code",
    "course__local_classification"
  ],
}

const SingleViewCourse = () => {
    
  const { t } = useTranslation()

  const [size] = useBreakpoint()
  const { courseID } = useParams()
  const { user } = useUser()
  let location = useLocation()
  const navigate = useNavigate()

  const formRef = useRef(null)

  const [courseData, setCourseData] = useState({})
  const [courseForm, setCourseForm] = useState({})
  const [fieldOptions, setFieldOptions] = useState({})
  const [validated, setValidated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState("")
  const [formData, setFormData] = useState({})

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [loadCourse, { 
    calledGetCourse, 
    loading: loadingGetCourse, 
    data: dataGetCourse, 
    error: errorGetCourse,
    refetch: refetchCourse }
  ] = useLazyQuery(GET_COURSE, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Course by ID data received")

      console.log(data.courseCC)
      setCourseData(data.courseCC)
    },
    onError: (error) => {
      console.error("Error loading Course by ID")
    }
  })

  const [loadGetCourseForm, { 
    loading: loadingGetCourseForm, dataGetCourseForm, errorGetCourseForm }
  ] = useLazyQuery(GET_COURSE_FORM, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programme Form data received")

      // Save form into session storage
      sessionStorage.setItem("courseForm", JSON.stringify(data.getCourseForm))

      // Save the fields form schema with type select in a separate object
      const fieldOptionsObj = {}
      Object.entries(data.getCourseForm).forEach(([key, field]) => {
        const type = field?.element?.value?.type

        if(type === "select") {
          fieldOptionsObj[key] = field?.element?.value?.options
        }

      })

      setCourseForm(data.getCourseForm)
      setFieldOptions(fieldOptionsObj)
    },
    onError: (error) => {
      console.error("Error loading Course Form")
    }
  })

  const [callEditCourse, { 
    loading: loadingEditCourse, dataEditCourse }
  ] = useMutation(EDIT_COURSE, {
    onCompleted: (data) => {
      console.log("Course modified")
    },
    onError: (error) => {
      console.error("Error editing Programme")
    }
  })

  const handleFormChange = (data) => {
    setFormData(data)
  }

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleEdit = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading('Updating Course', toastConfiguration)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    let cleanedData = convertCourseStringToNumbers(formData)
    cleanedData.id = courseData.id
    cleanedData.uuid = courseData.uuid
    cleanedData.status = courseData.status

    // Check if the data has changed
    // If yes, run the query
    // if not, show an error message
    if(!isDeepEqual(courseData, cleanedData)) {
      try {
        let result = {}

        result = await callEditCourse({
          variables: {
            courseData: getPatchDiff(courseData, cleanedData),
            baseUrl,
            apiKey,
            heiUuid: backendHeiUuid
          }
        })

        setValidated(false)
        
        if (!result?.errors) {

          toast.update(toastId, {
            ...toastConfiguration,
            render: `Programme ${cleanedData.title[0].string} updated`,
            type: "success",
            isLoading: false,
          })
          
          const { data: refreshedCourse } = await refetchCourse({
            variables: { baseUrl, apiKey }
          })

          setCourseData(refreshedCourse.courseCC)

          setShowModal(false)

        } else {

          console.error("Edit Course failed.")

          toast.update(toastId, {
            ...toastConfiguration,
            render: "Could not edit Course. " + result?.errors,
            type: "error",
            isLoading: false,
          })
        }

      } catch (err) {
        
        console.error("Edit Course failed:", err)

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not edit Course. Try again later.",
          type: "error",
          isLoading: false,
        })
      }

    } else {
      toast.update(toastId, {
        ...toastConfiguration,
        render: "There are no changes in the data.",
        type: "error",
        isLoading: false,
      })
    }

  }

  // Get Course
  useEffect(() => {

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const courseForm = JSON.parse(sessionStorage.getItem('courseForm'))

    if(!courseForm){
      loadGetCourseForm({
        variables: { 
          baseUrl, 
          apiKey,
          heiId: user?.heiID
        }
      })
    } else {
      // Save the fields form schema with type select in a separate object
      const fieldOptionsObj = {}
      Object.entries(courseForm).forEach(([key, field]) => {
        const type = field?.element?.value?.type

        if(type === "select") {
          fieldOptionsObj[key] = field?.element?.value?.options
        }

      })

      setFieldOptions(fieldOptionsObj)
      setCourseForm(courseForm)
    }

    if(userHasPermission
      && (heiConnectionStorage 
      || userConnectionStorage)){
      loadCourse({
        variables: {
          courseID,
          baseUrl,
          apiKey
        }
      })
    } else {
      navigate('/cc')
      return
    }

  }, [loadCourse])

  if (errorGetCourse)
    return (
      <div className="text-center text-danger mx-auto mt-5 pt-5">{'Error loading course'}</div>
    )

  if (loadingGetCourse) {
    return (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)"
        }}
      >
        <Spinner
          as="div"
          animation="grow"
          size="sm"
          role="status"
          aria-hidden="true"
          className="align-middle my-auto me-2"
        />
        <span>{'Loading'}</span>
      </div>
    )
  }

  return (
    <>
      <Container>
        <Row className="mt-5 pt-5 d-flex justify-content-between">
          <Col md={2}>
            <Link to="/cc/courses" className="goBackLink">
              <FontAwesomeIcon
                icon={faChevronLeft}
                color={COLORS.greyText}
                className="with-icon me-1"
                id={"smallIcon"}
              />
              {"Go back"}
            </Link>
          </Col>
          <Col md={3} className="d-flex justify-content-end">
            <CustomButton type="submit" buttonType={"primary"} icon={faPen} text={'edit'} handleClick={() => {
              setSelectedAction("edit")
              setValidated(false)
              setShowModal(true)
            }} hasIcon styles={{ marginLeft: 20 }} small />
          </Col>
        </Row>
      </Container>

      <Container>
        <div className="mt-4 pt-4">
          <Tabs id="general">
            <CustomTab eventKey="general" title="GENERAL" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.general.map((key, idx) => {
                  const value = courseData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-2">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry?.value || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, courseData, fieldOptions)}
                                {lang.length !== 0 && <span className="ms-2">({lang})</span>}
                              </div>
                            )
                          })
                        ) : value && value?.data && Array.isArray(value?.data) && value?.data.length > 0 ? (
                          value?.data.map((entry, i) => (
                            <div key={`entry-${idx}-${i}`} className="my-2">
                              {parseEntityValue(key, entry?.title, courseData, fieldOptions)}
                            </div>
                          ))
                        ) : (
                          parseEntityValue(
                            key, 
                            Array.isArray(value?.data) && value?.data.length === 0 ? null : value, 
                            courseData, 
                            fieldOptions
                          )
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="curriculum">
            <CustomTab eventKey="curriculum" title="CURRICULUM" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.curriculum.map((key, idx) => {
                  const value = courseData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-2">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry?.value || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, courseData, fieldOptions)}
                                {lang.length !== 0 && <span className="ms-2">({lang})</span>}
                              </div>
                            )
                          })
                        ) : value && value?.data && Array.isArray(value?.data) && value?.data.length > 0 ? (
                          value?.data.map((entry, i) => (
                            <div key={`entry-${idx}-${i}`} className="my-2">
                              {parseEntityValue(key, entry?.title, courseData, fieldOptions)}
                            </div>
                          ))
                        ) : (
                          parseEntityValue(
                            key, 
                            Array.isArray(value?.data) && value?.data.length === 0 ? null : value, 
                            courseData, 
                            fieldOptions
                          )
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="academic">
            <CustomTab eventKey="academic" title="ACADEMIC" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.academic.map((key, idx) => {
                  const value = courseData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-2">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry?.value || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, courseData, fieldOptions)}
                                {lang.length !== 0 && <span className="ms-2">({lang})</span>}
                              </div>
                            )
                          })
                        ) : value && value?.data && Array.isArray(value?.data) && value?.data.length > 0 ? (
                          value?.data.map((entry, i) => (
                            <div key={`entry-${idx}-${i}`} className="my-2">
                              {parseEntityValue(key, entry?.title, courseData, fieldOptions)}
                            </div>
                          ))
                        ) : (
                          parseEntityValue(
                            key, 
                            Array.isArray(value?.data) && value?.data.length === 0 ? null : value, 
                            courseData, 
                            fieldOptions
                          )
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="prerequisites">
            <CustomTab eventKey="prerequisites" title="PREREQUISITES" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.prerequisites.map((key, idx) => {
                  const value = courseData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-2">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry?.value || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, courseData, fieldOptions)}
                                {lang.length !== 0 && <span className="ms-2">({lang})</span>}
                              </div>
                            )
                          })
                        ) : value && value?.data && Array.isArray(value?.data) && value?.data.length > 0 ? (
                          value?.data.map((entry, i) => (
                            <div key={`entry-${idx}-${i}`} className="my-2">
                              {parseEntityValue(key, entry?.title, courseData, fieldOptions)}
                            </div>
                          ))
                        ) : (
                          parseEntityValue(
                            key, 
                            Array.isArray(value?.data) && value?.data.length === 0 ? null : value, 
                            courseData, 
                            fieldOptions
                          )
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="classification">
            <CustomTab eventKey="classification" title="CLASSIFICATION" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.classification.map((key, idx) => {
                  const value = courseData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-2">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry?.value || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, courseData, fieldOptions)}
                                {lang.length !== 0 && <span className="ms-2">({lang})</span>}
                              </div>
                            )
                          })
                        ) : value && value?.data && Array.isArray(value?.data) && value?.data.length > 0 ? (
                          value?.data.map((entry, i) => (
                            <div key={`entry-${idx}-${i}`} className="my-2">
                              {parseEntityValue(key, entry?.title, courseData, fieldOptions)}
                            </div>
                          ))
                        ) : (
                          parseEntityValue(
                            key, 
                            Array.isArray(value?.data) && value?.data.length === 0 ? null : value, 
                            courseData, 
                            fieldOptions
                          )
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <CustomModal
          show={showModal}
          handleClose={() => {
            setShowModal(false)
            setValidated(false)
          }}
          disabledCancelButton={
            loadingEditCourse
          }
          body={
            (selectedAction === "edit") && (
              <div>
                
                <Form ref={formRef} noValidate validated={validated}>
                  <DynamicForm 
                    schema={courseForm}
                    onChange={handleFormChange}
                    defaultValues={selectedAction === 'edit' ? courseData : {}}
                  />
                </Form>
                
              </div>
            )
          }
          button={
            selectedAction === "edit" && ( 
              <CustomButton
                buttonType={"primary"}
                disabled={loadingEditCourse}
                text={'Save Changes'}
                icon={faFloppyDisk}
                hasIcon={true}
                iconColor={COLORS.white}
                handleClick={handleEdit}
              />
            )
          }
          title={
            selectedAction === "edit" && (
              'Edit an existing Course'
            )
          }
          subtitle={
            selectedAction === "edit" && (
              'Add the necessary information to edit a Course'
            )
          }
          hasIcon
          fullscreen
        />
      </Container>

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default SingleViewCourse